import type { CatalogResultCard } from './types'

/** A query "looks like" a part number when it carries a digit or a dash and
 * no whitespace — the same cheap gate the design source's `pnLookup` uses
 * (`Fictiv Platform.dc.html`, `runSearch`/`pnLookup`) before it bothers
 * checking the query against any real SKU. A bare word ("bearings") never
 * qualifies, so it falls through to the category-match path below even if it
 * happens to also match nothing. */
const PART_NUMBER_HINT = /[-0-9]/
const WHITESPACE = /\s/

export function looksLikePartNumber(query: string): boolean {
  const trimmed = query.trim()
  return trimmed.length > 0 && PART_NUMBER_HINT.test(trimmed) && !WHITESPACE.test(trimmed)
}

/**
 * The "exact part number" branch of search routing (README §8 "Search
 * routing" / UID-251): a query that looks like a part number and exactly
 * matches (case-insensitively) — or, failing that, is contained in — one of
 * the search's own returned cards' `catalogSku`. Mirrors the design source's
 * `pnLookup` fallback from an exact match to a substring match. Returns
 * `null` for a query that doesn't look like a part number at all, so a
 * bare-word query that happens to substring-match some unrelated SKU is
 * never misrouted here.
 */
export function findExactPartMatch(
  query: string,
  items: readonly CatalogResultCard[],
): CatalogResultCard | null {
  if (!looksLikePartNumber(query)) return null
  const q = query.trim().toLowerCase()
  const bySku = (item: CatalogResultCard) => item.sourceable.catalogSku?.toLowerCase()
  return (
    items.find((item) => bySku(item) === q) ??
    items.find((item) => bySku(item)?.includes(q)) ??
    null
  )
}

/**
 * The "term" branch of search routing: the catalog category shared by the
 * most matched cards, so a keyword search routes to the single
 * best-matching leaf-category selector (mirrors the design source's
 * `runSearch` tallying `f.sub` across the matched families and picking the
 * highest count). `null` when no matched card carries a `category` at all —
 * the caller falls back to the no-results state rather than guessing.
 */
export function bestMatchingCategory(items: readonly CatalogResultCard[]): string | null {
  const counts = new Map<string, number>()
  for (const item of items) {
    if (!item.category) continue
    counts.set(item.category, (counts.get(item.category) ?? 0) + 1)
  }
  let best: string | null = null
  let bestCount = 0
  for (const [category, count] of counts) {
    if (count > bestCount) {
      best = category
      bestCount = count
    }
  }
  return best
}

/** Where a resolved search should navigate to (README §8 "Search routing").
 * `null` means "stay here and show the no-results state" — there is nothing
 * to route to. */
export type SearchRouteTarget =
  | { readonly kind: 'series'; readonly category: string; readonly seriesCode: string }
  | { readonly kind: 'category'; readonly category: string }
  | { readonly kind: 'part'; readonly sku: string }

/**
 * The single routing decision this screen makes once a search resolves
 * (README §8): a part-number query that exactly matches a returned card
 * deep-links straight to that card's series page — `CatalogSeriesConfigurePage`,
 * the real per-series "parts table" (ADR-0097/0098/0099) — when the matched
 * card carries a `seriesCode` (a plain, non-enriched catalog row omits it;
 * ADR-0091 "omit, don't stub"). An exact-part match with no `seriesCode` —
 * ~25 business-critical MISUMI SKUs (e.g. `CB5-14`, `SH-FRSNUT5`,
 * `HOSBS19N`) plus ~31k other seed rows, none of which have a linked
 * `catalog_series` row today — has no family to route to at all, so it
 * returns `{ kind: 'part' }` instead: the caller stays on this
 * search-results screen and shows/highlights that one matched card, rather
 * than dropping the user on a category-wide series grid for a part they
 * asked for by exact SKU (verified against the design source's own
 * `pnLookup`/`runSearch` fallback in `Fictiv Platform.dc.html`, which does
 * exactly this — never a fabricated flat listing; see
 * `docs/design/catalog-configurator-surfaces-reconciliation.md` §0).
 * Anything else with a matched category (a keyword/term match) routes to
 * that category's series-card grid, `CatalogSeriesPage` (the "new category
 * selector," UID-286). `null` when nothing in the results carries a
 * category at all and there's no exact-part match either.
 */
export function resolveSearchRoute(
  query: string,
  items: readonly CatalogResultCard[],
): SearchRouteTarget | null {
  const exactMatch = findExactPartMatch(query, items)
  if (exactMatch) {
    if (exactMatch.seriesCode && exactMatch.category) {
      return { kind: 'series', category: exactMatch.category, seriesCode: exactMatch.seriesCode }
    }
    const sku = exactMatch.sourceable.catalogSku
    if (sku) return { kind: 'part', sku }
  }
  const category = exactMatch?.category ?? bestMatchingCategory(items)
  return category ? { kind: 'category', category } : null
}
